export interface CreateUserDto {
    firebaseUid: string;
    email: string;
    firstName: string;
    lastName: string;
    role: number;
    photoURL: string;
    isActive: boolean;
    jobPosition?: string;
    hubspotUserToken?: string;
    verificationToken?: string;
}

export interface CreateBusinessAccountDto {
    companyName: string;
    website: string;
    instagram: string;
    tiktok: string;
    about: string;
    coverPhoto: string;
    companyLogo: string;
    creatorId: string;
    isActive: boolean;
    stripeCustomerId?: string;
    currencyCode: string;
    isPayg: boolean;
    industries?: brandFetchIndustryDto[];
    vatNumber: string;
    agencyId?: string;
    toolOnly?: boolean;
}

export interface brandFetchIndustryDto {
    emoji: string,
    id: string
    name: string,
    parent?: { emoji: string, id: string, name: string, slug: string }
    score: number
    slug: string
}

export enum BusinessRegistrationTypeEnum {
    Agency = 1,
    Brand = 2,
    Creator = 3,
}

export enum UserRolesEnum {
    "Basic" = 0,
    "Super Admin" = 1,
    "Brand Admin" = 2,
    "Agency Admin" = 3,
    "SHOUT Campaign Manager" = 4,
    "Campaign Manager" = 5
}

/**
 * Access roles for SHOUT-internal-only actions.
 *
 * These are raw values from the backend's `UserAccessRolesEnum`
 * (UniCreate-ligher-API/src/users/dto/user-access-roles.enum.ts), which is what populates
 * `UserAccount.accessRoles`. Do NOT use `UserRolesEnum` above for these: its names disagree
 * with the backend (it calls 1 "Super Admin" where the backend uses 1 for Wallet and 2 for
 * GodMode), so using it here would grant the wrong role.
 *
 *   6 = CreatorAdmin — the role gating the "Admin Settings" button on view-submission and
 *       the SHOUT Admin Options page.
 *   2 = GodMode      — superadmin safety net, included by every guard in the backend.
 *
 * The backend enforces this same set; this constant only controls UI visibility.
 */
export const SHOUT_INTERNAL_ACCESS_ROLES: number[] = [6, 2];

export interface UserAccount {
    firebaseUid: string;
    email: string;
    firstName: string;
    lastName: string;
    jobPosition: string;
    role: number;
    accessRoles: number[];
    photoURL: string;
    isActive: boolean;
    streamIoToken: string;
    inviteAccepted: boolean;
    createdDate?: Date;
    latest_user_visit?: Date;
    preferredPlatform?: string;
    test_user?: boolean;
}

export interface InviteUserDto {
    email: string;
    firstName: string;
    lastName: string;
    role: number;
    photoURL: string;
    isActive: boolean;
    businessAccountId: string;
    jobPosition?: string;
}

export interface InviteAgencyUserDto {
    email: string;
    firstName: string;
    lastName: string;
    role: number;
    photoURL: string;
    isActive: boolean;
    agencyId: string;
    businessAccounts: string[];
}

/**
 * Result of POST /business-accounts/invite-user and POST /agencies/invite-user.
 *
 * Every field is optional so an older API deploy (which returned the bare `User`) degrades to
 * neutral messaging rather than rendering `undefined` at the user.
 */
export interface InviteUserResultDto {
    user?: Partial<UserAccount>;
    /** 'existing' means the person already had a SHOUT account and was attached to it. */
    userState?: 'created' | 'existing';
    firebaseState?: 'created' | 'existing';
    /** Membership row id — business_user (brand invite). */
    businessUserId?: string;
    /** Membership row id — agency_user (agency invite). */
    agencyUserId?: string;
    /** Brands the user was additionally linked to (agency invite). */
    addedBusinessAccountIds?: string[];
    /** false when the member was added but the invite email could not be sent. */
    emailQueued?: boolean;
    emailType?: 'new-credentials' | 'password-reset' | 'none';
    /** UniTaskr user-app registration; 'failed' is non-fatal. */
    uniAppSync?: 'ok' | 'failed';
}

export interface UserVisitDto {
    isDeleted: boolean;
    id: string;
    createdDate: Date;
    userId: string;
    dateVisited: Date;
    requestAgent: string;
    deviceInfo: DeviceInfoDto;
    platform: any;
    user: UserAccount;
}

export interface DeviceInfoDto {
    browser: string;
    browser_version: string;
    device: string;
    deviceType: string;
    orientation: string;
    os: string;
    os_version: string;
    userAgent: string;
}